/**
 * «Dinero» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of
 * it»): the money there is now and where, what the next thirty days should bring in and take out,
 * and the day it runs lowest; and the money it takes to run the shop, how long it stays on the
 * shelf and in the fiado, and how long the suppliers wait for theirs.
 *
 * Money is what the books hold in the accounts that are money (`CUENTAS_EFECTIVO`). The days ahead
 * are what the shop has been doing: each weekday's usual sales (`ventaUsualPorDiaSemana`), the part
 * of them paid on the spot, the fiado collected a day, what was sold put back on the shelf at the
 * margin the shop makes; and what has a day of its own: the payroll's paydays, the fixed expenses,
 * the purchases and the expenses on credit. Pure, so the screen and its tests share one answer.
 */

import { PAGOS_POR_ANIO, diasEntre, sumarDias, type Centavos, type FrecuenciaPago, type Rango } from '@abastece/shared';
import {
  CUENTAS_EFECTIVO,
  calcularPlanilla,
  pagoDe,
  pendienteDeGasto,
  provisionesDe,
  type Compra,
  type Empleado,
  type Gasto,
  type LineaReporte,
  type PayrollRates,
  type Planilla,
} from '@abastece/core';
import { montoAcordado, rangoSiguiente, rangoSugerido } from './planilla.js';

/** The days «Dinero» looks ahead. */
export const DIAS_DE_PRONOSTICO = 30;

/** What is owed now and has no day in the app: the payroll's net not handed over, INSS, INATEC and the income tax withheld. */
export const DEUDAS_SIN_FECHA: readonly string[] = ['2250', '2220', '2230', '2240'];

export type Concepto = 'ventas' | 'cobros' | 'reponer' | 'planilla' | 'fijos' | 'compras' | 'gastos' | 'deudas';

/** Something that leaves on a day of its own. */
export interface Salida {
  fecha: string;
  concepto: Extract<Concepto, 'planilla' | 'fijos' | 'compras' | 'gastos' | 'deudas'>;
  monto: number;
  /** Whose: the fixed expense, the supplier, the payroll's run. */
  detalle: string;
}

export interface DiaDePronostico {
  fecha: string;
  entra: number;
  sale: number;
  /** The money there would be at the day's end. */
  saldo: number;
}

export interface Pronostico {
  dias: DiaDePronostico[];
  inicial: number;
  final: number;
  /** The day the money runs lowest, and how low. */
  minimo: { fecha: string; saldo: number };
  /** What each comes to over the days, coming in positive and going out negative. */
  porConcepto: Record<Concepto, number>;
}

/**
 * The money day by day for `dias` days from today: what there is now, plus each day's usual sales
 * paid on the spot and the fiado collected a day, less what was sold put back at cost and what
 * falls due that day. Today counts what is left of its usual sales; anything due before today is
 * taken today.
 */
export function pronosticoDeCaja(d: {
  hoy: string;
  dias?: number;
  saldoInicial: number;
  /** What a day of each weekday usually sells, Sunday first. */
  ventaPorDiaSemana: readonly number[];
  /** What today has sold so far. */
  vendidoHoy: number;
  /** The part of the sales paid on the spot, in basis points: the rest is fiado. */
  contadoBp: number;
  /** The gross margin, in basis points: what was sold is put back at the rest. */
  margenBp: number;
  cobrosPorDia: number;
  salidas: readonly Salida[];
}): Pronostico {
  const n = d.dias ?? DIAS_DE_PRONOSTICO;
  const porConcepto: Record<Concepto, number> = { ventas: 0, cobros: 0, reponer: 0, planilla: 0, fijos: 0, compras: 0, gastos: 0, deudas: 0 };
  const dias: DiaDePronostico[] = [];
  let saldo = d.saldoInicial;
  let minimo: { fecha: string; saldo: number } | null = null;
  for (let i = 0; i < n; i++) {
    const fecha = sumarDias(d.hoy, i);
    const usual = d.ventaPorDiaSemana[new Date(`${fecha}T00:00:00Z`).getUTCDay()] ?? 0;
    const venta = i === 0 ? Math.max(0, usual - d.vendidoHoy) : usual;
    const contado = (venta * d.contadoBp) / 10_000;
    const reponer = (venta * (10_000 - d.margenBp)) / 10_000;
    const ese = d.salidas.filter((s) => (i === 0 ? s.fecha <= fecha : s.fecha === fecha));
    for (const s of ese) porConcepto[s.concepto] -= s.monto;
    porConcepto.ventas += contado;
    porConcepto.cobros += d.cobrosPorDia;
    porConcepto.reponer -= reponer;
    const entra = contado + d.cobrosPorDia;
    const sale = reponer + ese.reduce((a, s) => a + s.monto, 0);
    saldo += entra - sale;
    dias.push({ fecha, entra, sale, saldo });
    if (minimo === null || saldo < minimo.saldo) minimo = { fecha, saldo };
  }
  return { dias, inicial: d.saldoInicial, final: saldo, minimo: minimo ?? { fecha: d.hoy, saldo: d.saldoInicial }, porConcepto };
}

/** The `detalle` of the aguinaldo's outflow, among the payroll's (`pagosDePlanilla`). */
export const DETALLE_AGUINALDO = 'aguinaldo';

/** The aguinaldo's next due day from `hoy` on: 10 December (Art. 93 CT, SPEC §3). */
export function vencimientoDelAguinaldo(hoy: string): string {
  const este = `${hoy.slice(0, 4)}-12-10`;
  return hoy <= este ? este : `${Number(hoy.slice(0, 4)) + 1}-12-10`;
}

/**
 * The payroll's paydays in the days ahead: for each schedule, the runs from where the last one left
 * off (`rangoSugerido`), each paid on its last day, a run already due paid today. Each person on a
 * fixed pay at what the run costs the shop in cash: the wage and the shop's INSS and INATEC, not the
 * aguinaldo and vacations it accrues, which are paid when they fall due. Who is paid by the day costs
 * what they work, which nobody knows before: they are counted apart.
 *
 * And the aguinaldo, on 10 December when that is among the days (2026-10-20): what is set aside for
 * the period that ends on 30 November, and what the runs still to come in it will set aside. It was
 * never there, though the accrual exists to say what December owes: seen on 20 November, the days
 * to 10 December missed a month's salary a person.
 */
export function pagosDePlanilla(d: {
  empleados: readonly Empleado[];
  corridas: readonly Planilla[];
  rates: PayrollRates;
  hoy: string;
  hasta: string;
}): { salidas: Salida[]; porDia: number } {
  const salidas: Salida[] = [];
  let porDia = 0;
  const activos = d.empleados.filter((e) => e.activo && (e.fechaSalida === null || e.fechaSalida > d.hoy));
  // The aguinaldo due next, of the period that ends the day before the next one starts.
  const vence = vencimientoDelAguinaldo(d.hoy);
  const inicioDelSiguiente = `${vence.slice(0, 4)}-12-01`;
  let porVenir = 0;
  for (const frecuencia of ['semanal', 'quincenal', 'mensual'] as const satisfies readonly FrecuenciaPago[]) {
    const deEsta = activos.filter((e) => pagoDe(e).frecuencia === frecuencia);
    const fijos = deEsta.filter((e) => !pagoDe(e).porDia);
    porDia += deEsta.length - fijos.length;
    if (fijos.length === 0) continue;
    const costo = fijos.reduce((a, e) => {
      const pago = pagoDe(e);
      return a + calcularPlanilla({ bruto: montoAcordado(e) }, d.rates, { pagosPorAnio: PAGOS_POR_ANIO[frecuencia], conInss: pago.conInss, conPrestaciones: pago.conPrestaciones }).costoTotal;
    }, 0);
    const apartan = fijos.reduce((a, e) => a + provisionesDe(montoAcordado(e), pagoDe(e).conPrestaciones).provisionAguinaldo, 0);
    for (let r: Rango = rangoSugerido(frecuencia, d.corridas, d.hoy), vez = 0; r.hasta <= d.hasta && vez < 60; r = rangoSiguiente(frecuencia, r), vez++) {
      salidas.push({ fecha: r.hasta < d.hoy ? d.hoy : r.hasta, concepto: 'planilla', monto: costo, detalle: `${r.desde}|${r.hasta}` });
      if (r.hasta < inicioDelSiguiente) porVenir += apartan;
    }
  }
  if (vence <= d.hasta) {
    // What runs of the next period already set aside is not due this December.
    const delSiguiente = d.corridas
      .filter((p) => !p.esLiquidacion && p.periodo >= inicioDelSiguiente.slice(0, 7))
      .flatMap((p) => p.lineas)
      .filter((l) => activos.some((e) => e.id === l.empleadoId))
      .reduce((a, l) => a + l.provisionAguinaldo, 0);
    const apartado = activos.reduce((a, e) => a + e.aguinaldoAcumulado, 0) - delSiguiente;
    const monto = apartado + porVenir;
    if (monto > 0) salidas.push({ fecha: vence, concepto: 'planilla', monto, detalle: DETALLE_AGUINALDO });
  }
  return { salidas, porDia };
}

/**
 * What is owed on the purchases bought on credit, each on the day it falls due: one already due, or
 * with no day, taken today.
 */
export function comprasPorPagar(compras: readonly Compra[], hoy: string, hasta: string): Salida[] {
  return compras.flatMap((c) => {
    const debe = c.totalCordobas - c.pagado - (c.devueltoACuenta ?? 0);
    if (c.anulada || debe <= 0) return [];
    const fecha = c.vencePago && c.vencePago > hoy ? c.vencePago : hoy;
    return fecha <= hasta ? [{ fecha, concepto: 'compras' as const, monto: debe, detalle: c.proveedorId }] : [];
  });
}

/**
 * What is still owed of the expenses bought on credit — a repair, a bill the supplier waits for —
 * taken today: an expense has no day it falls due. They were left out, and C$ 5,000 owed against
 * C$ 3,000 in hand had the lowest day at C$ 3,000, and «Hoy» said nothing (2026-10-13).
 */
export function gastosPorPagar(gastos: readonly Gasto[], hoy: string): Salida[] {
  return gastos.flatMap((g) => {
    const debe = pendienteDeGasto(g);
    return debe > 0 ? [{ fecha: hoy, concepto: 'gastos' as const, monto: debe, detalle: g.descripcion }] : [];
  });
}

/** What the books owe now with no day of its own, taken today. */
export function deudasSinFecha(pasivos: readonly LineaReporte[], hoy: string): Salida[] {
  return pasivos.filter((l) => DEUDAS_SIN_FECHA.includes(l.codigo) && l.monto > 0).map((l) => ({ fecha: hoy, concepto: 'deudas' as const, monto: l.monto, detalle: l.codigo }));
}

// ---------------------------------------------------------------------------
// Capital de trabajo
// ---------------------------------------------------------------------------

export interface CapitalDeTrabajo {
  dinero: Centavos;
  inventario: Centavos;
  fiado: Centavos;
  /** What else the shop holds that turns into money soon: advances, airtime, transfers to confirm. */
  otros: Centavos;
  /** What it owes: the suppliers and the rest (payroll, taxes). */
  proveedores: Centavos;
  otrasDeudas: Centavos;
  capital: Centavos;
  /** How many days the goods stay on the shelf, the fiado goes unpaid, the suppliers wait; null with nothing to measure. */
  diasInventario: number | null;
  diasCobro: number | null;
  diasPago: number | null;
  /** How long a córdoba spent on goods takes to come back: shelf and fiado, less what the suppliers wait. */
  ciclo: number | null;
}

/**
 * The money it takes to run the shop: what it holds that is or soon becomes money (cash, goods,
 * fiado) less what it owes soon; and how long the money stays out: the goods at what the last days
 * cost to sell, the fiado at what they gave on credit, the suppliers at what they sold on credit.
 */
export function capitalDeTrabajo(d: {
  activos: readonly LineaReporte[];
  pasivos: readonly LineaReporte[];
  /** The last days': what what was sold cost, what was given on credit, what was bought on credit, and how many days. */
  costoDeVentas: number;
  fiadoDado: number;
  comprasACredito: number;
  dias: number;
}): CapitalDeTrabajo {
  const de = (lineas: readonly LineaReporte[], codigos: readonly string[]) => lineas.filter((l) => codigos.includes(l.codigo)).reduce((a, l) => a + l.monto, 0);
  const dinero = de(d.activos, CUENTAS_EFECTIVO);
  const inventario = de(d.activos, ['1200', '1210', '1220']);
  const fiado = de(d.activos, ['1300']);
  const otros = de(d.activos, ['1310', '1350', '1400']);
  const proveedores = de(d.pasivos, ['2100']);
  const otrasDeudas = d.pasivos.reduce((a, l) => a + l.monto, 0) - proveedores;
  const porDia = (x: number) => (d.dias > 0 && x > 0 ? x / d.dias : 0);
  const dias = (saldo: number, alDia: number) => (alDia > 0 ? Math.round(Math.max(0, saldo) / alDia) : null);
  const diasInventario = dias(inventario, porDia(d.costoDeVentas));
  const diasCobro = dias(fiado, porDia(d.fiadoDado));
  const diasPago = dias(proveedores, porDia(d.comprasACredito));
  return {
    dinero: dinero as Centavos,
    inventario: inventario as Centavos,
    fiado: fiado as Centavos,
    otros: otros as Centavos,
    proveedores: proveedores as Centavos,
    otrasDeudas: otrasDeudas as Centavos,
    capital: (dinero + inventario + fiado + otros - proveedores - otrasDeudas) as Centavos,
    diasInventario,
    diasCobro,
    diasPago,
    ciclo: diasInventario === null ? null : diasInventario + (diasCobro ?? 0) - (diasPago ?? 0),
  };
}

/** The days between two dates the pace was measured over, both in, a week at least. */
export const diasMedidos = (desde: string, hasta: string): number => Math.max(7, diasEntre(desde, hasta) + 1);
